import { Suspense, useEffect, useState } from 'react';
import { Await } from 'react-router';

import { cn } from '~/lib/utils';

type NotificationBadgeProps = {
  promise: Promise<number>;
  storageKey: string;
  className?: string;
};

function getSessionStorageValue(key: string): number {
  if (typeof window === 'undefined') return 0;
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const parsed = parseInt(cached, 10);
      return isNaN(parsed) ? 0 : parsed;
    }
  } catch {
    // sessionStorage may throw in restricted contexts (e.g., incognito, disabled storage)
  }
  return 0;
}

function setSessionStorageValue(key: string, value: number): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, value.toString());
  } catch {
    // sessionStorage may throw in restricted contexts
  }
}

function formatCount(count: number): string {
  if (count < 10) return count.toString();
  if (count < 100) return '9+';
  return '99+';
}

function NotificationCounter({ count, className }: { count: number; className?: string }) {
  return (
    <div
      className={cn(
        `
          inline-flex h-[18px] min-w-[18px] items-center justify-center
          rounded-full bg-blue-500 px-1 text-[11px] font-semibold text-white
        `,
        className
      )}
    >
      {formatCount(count)}
    </div>
  );
}

function NotificationBadgeContent({
  count,
  storageKey,
  className,
}: {
  count: number;
  storageKey: string;
  className?: string;
}) {
  const resolvedCount = count ?? 0;

  useEffect(() => {
    setSessionStorageValue(storageKey, resolvedCount);
  }, [storageKey, resolvedCount]);

  return resolvedCount > 0 ? (
    <NotificationCounter count={resolvedCount} className={className} />
  ) : null;
}

/**
 * A notification badge that displays the count number when count > 0.
 * Shows single digits (1-9), "9+" for 10-99, or "99+" for 100+.
 * Caches the count in sessionStorage for instant display while the promise resolves.
 */
export function NotificationBadge({ promise, storageKey, className }: NotificationBadgeProps) {
  const [cachedCount] = useState(() => getSessionStorageValue(storageKey));

  return (
    <Suspense
      fallback={
        cachedCount > 0 ? <NotificationCounter count={cachedCount} className={className} /> : null
      }
    >
      <Await resolve={promise}>
        {(count: number) => (
          <NotificationBadgeContent count={count} storageKey={storageKey} className={className} />
        )}
      </Await>
    </Suspense>
  );
}
